import { c as _c } from "react/compiler-runtime";
import { IcoDotsHorizontal16, IcoPencil16, IcoStar16, IcoStarFilled16, IcoTrash16, IcoWriting16 } from '@onefootprint/icons';
import type { ChatSession } from '@onefootprint/request-types/dashboard';
import { Dropdown, Shimmer, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
export type ChatHistoryMenuProps = {
  sessions: ChatSession[];
  isLoading: boolean;
  activeSessionId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onStar: (id: string, nextStarred: boolean) => void;
  onRename: (session: ChatSession) => void;
  onRemove: (session: ChatSession) => void;
};
const SHIMMER_WIDTHS = [180, 140, 200, 120, 160] as const;
const ChatHistoryMenu = t0 => {
  const $ = _c(27);
  const {
    sessions,
    isLoading,
    activeSessionId,
    onSelect,
    onNewChat,
    onStar,
    onRename,
    onRemove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "chatHistoryDropdown"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  if (isLoading) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Shimmer className="rounded-sm" height={12} width={56} />;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="flex flex-col gap-2 px-2 py-2">{t2}<div className="flex flex-col gap-2">{SHIMMER_WIDTHS.map(_temp)}</div></div>;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    return t3;
  }
  let t2;
  if ($[3] !== sessions) {
    t2 = sessions.filter(_temp2);
    $[3] = sessions;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const starred = t2;
  let t3;
  if ($[5] !== sessions) {
    t3 = sessions.filter(_temp3);
    $[5] = sessions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const recent = t3;
  let t4;
  if ($[7] !== t) {
    t4 = t(_temp4);
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="text-body-3 text-primary">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== onNewChat || $[12] !== t5) {
    t6 = <div className="px-1 pt-1"><Dropdown.Item size="default" iconLeft={IcoWriting16} onSelect={onNewChat} className="justify-start pl-2 gap-1.5! h-8!">{t5}</Dropdown.Item></div>;
    $[11] = onNewChat;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== activeSessionId || $[15] !== onRemove || $[16] !== onRename || $[17] !== onSelect || $[18] !== onStar || $[19] !== recent || $[20] !== sessions.length || $[21] !== starred || $[22] !== t) {
    t7 = sessions.length === 0 ? <p className="text-body-3 text-tertiary px-3 py-2">{t(_temp5)}</p> : <><div className="mt-2 mb-3 px-2"><Dropdown.Divider /></div>{starred.length > 0 && <Section label={t(_temp6)} items={starred} activeSessionId={activeSessionId} onSelect={onSelect} onStar={onStar} onRename={onRename} onRemove={onRemove} />}{recent.length > 0 && <Section label={t(_temp7)} items={recent} activeSessionId={activeSessionId} onSelect={onSelect} onStar={onStar} onRename={onRename} onRemove={onRemove} />}</>;
    $[14] = activeSessionId;
    $[15] = onRemove;
    $[16] = onRename;
    $[17] = onSelect;
    $[18] = onStar;
    $[19] = recent;
    $[20] = sessions.length;
    $[21] = starred;
    $[22] = t;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t6 || $[25] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
type SectionProps = {
  label: string;
  items: ChatSession[];
  activeSessionId: string | null;
  onSelect: (id: string) => void;
  onStar: (id: string, nextStarred: boolean) => void;
  onRename: (session: ChatSession) => void;
  onRemove: (session: ChatSession) => void;
};
const Section = t0 => {
  const $ = _c(18);
  const {
    label,
    items,
    activeSessionId,
    onSelect,
    onStar,
    onRename,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Dropdown.GroupTitle className="flex px-3 pt-2 pb-1 text-tertiary text-body-3">{label}</Dropdown.GroupTitle>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activeSessionId || $[3] !== items || $[4] !== onRemove || $[5] !== onRename || $[6] !== onSelect || $[7] !== onStar) {
    let t3;
    if ($[9] !== activeSessionId || $[10] !== onRemove || $[11] !== onRename || $[12] !== onSelect || $[13] !== onStar) {
      t3 = s => <ChatRow key={s.id} session={s} isActive={s.id === activeSessionId} onSelect={onSelect} onStar={onStar} onRename={onRename} onRemove={onRemove} />;
      $[9] = activeSessionId;
      $[10] = onRemove;
      $[11] = onRename;
      $[12] = onSelect;
      $[13] = onStar;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t2 = items.map(t3);
    $[2] = activeSessionId;
    $[3] = items;
    $[4] = onRemove;
    $[5] = onRename;
    $[6] = onSelect;
    $[7] = onStar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[15] !== t1 || $[16] !== t2) {
    t3 = <Dropdown.Group className="flex flex-col gap-0.5">{t1}{t2}</Dropdown.Group>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
};
type ChatRowProps = {
  session: ChatSession;
  isActive: boolean;
  onSelect: (id: string) => void;
  onStar: (id: string, nextStarred: boolean) => void;
  onRename: (session: ChatSession) => void;
  onRemove: (session: ChatSession) => void;
};
const ChatRow = t0 => {
  const $ = _c(79);
  const {
    session,
    isActive,
    onSelect,
    onStar,
    onRename,
    onRemove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "chatHistoryDropdown"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const [menuOpen, setMenuOpen] = useState(false);
  const isSandbox = !session.isLive;
  let t2;
  if ($[1] !== session.title || $[2] !== t) {
    t2 = session.title || t(_temp8);
    $[1] = session.title;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const displayName = t2;
  let t3;
  if ($[4] !== onSelect || $[5] !== session.id) {
    t3 = () => onSelect(session.id);
    $[4] = onSelect;
    $[5] = session.id;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isActive) {
    t4 = cx("justify-start gap-2 pl-2 pr-8 h-8!", {
      "bg-senary/70!": isActive
    });
    $[7] = isActive;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isSandbox || $[10] !== t) {
    t5 = isSandbox ? t(_temp9) : t(_temp10);
    $[9] = isSandbox;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isSandbox || $[13] !== t) {
    t6 = isSandbox ? t(_temp11) : t(_temp12);
    $[12] = isSandbox;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = isSandbox ? "bg-warning-inverted" : "bg-success-inverted";
  let t8;
  if ($[15] !== t7) {
    t8 = cx("block rounded-full size-1.5", t7);
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <span className={t8} />;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t6 || $[20] !== t9) {
    t10 = <span role="img" className="flex items-center justify-center size-3 shrink-0" aria-label={t6}>{t9}</span>;
    $[19] = t6;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t5) {
    t11 = <Tooltip content={t5} side="left">{t10}</Tooltip>;
    $[22] = t10;
    $[23] = t5;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== displayName) {
    t12 = <span className="text-body-3 text-primary truncate flex-1 ml-1">{displayName}</span>;
    $[25] = displayName;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t3 || $[30] !== t4) {
    t13 = <Dropdown.Item size="default" onSelect={t3} className={t4}>{t11}{t12}</Dropdown.Item>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t3;
    $[30] = t4;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t) {
    t14 = t(_temp13);
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const t15 = menuOpen ? "bg-senary/70" : "hover:bg-hover-control";
  const t16 = isActive || menuOpen ? "opacity-100" : "opacity-0 max-md:opacity-100 group-hover:opacity-100";
  let t17;
  if ($[34] !== t15 || $[35] !== t16) {
    t17 = cx("flex items-center justify-center w-7 h-7 rounded-(--icon-button-border-radius) cursor-pointer", t15, t16);
    $[34] = t15;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <IcoDotsHorizontal16 />;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t14 || $[39] !== t17) {
    t19 = <Dropdown.Trigger asChild={true}><button type="button" aria-label={t14} className={t17}>{t18}</button></Dropdown.Trigger>;
    $[38] = t14;
    $[39] = t17;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = session.isStarred ? IcoStarFilled16 : IcoStar16;
  let t21;
  if ($[41] !== onStar || $[42] !== session.id || $[43] !== session.isStarred) {
    t21 = () => onStar(session.id, !session.isStarred);
    $[41] = onStar;
    $[42] = session.id;
    $[43] = session.isStarred;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== session.isStarred || $[46] !== t) {
    t22 = session.isStarred ? t(_temp14) : t(_temp15);
    $[45] = session.isStarred;
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t20 || $[49] !== t21 || $[50] !== t22) {
    t23 = <Dropdown.Item size="compact" iconLeft={t20} onSelect={t21}>{t22}</Dropdown.Item>;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== onRename || $[53] !== session) {
    t24 = () => onRename(session);
    $[52] = onRename;
    $[53] = session;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t) {
    t25 = t(_temp16);
    $[55] = t;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t24 || $[58] !== t25) {
    t26 = <Dropdown.Item size="compact" iconLeft={IcoPencil16} onSelect={t24}>{t25}</Dropdown.Item>;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== onRemove || $[61] !== session) {
    t27 = () => onRemove(session);
    $[60] = onRemove;
    $[61] = session;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t) {
    t28 = t(_temp17);
    $[63] = t;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t27 || $[66] !== t28) {
    t29 = <Dropdown.Item size="compact" iconLeft={IcoTrash16} destructive={true} onSelect={t27}>{t28}</Dropdown.Item>;
    $[65] = t27;
    $[66] = t28;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t23 || $[69] !== t26 || $[70] !== t29) {
    t30 = <Dropdown.Portal><Dropdown.Content align="end" sideOffset={4} className="w-[180px] p-1">{t23}{t26}{t29}</Dropdown.Content></Dropdown.Portal>;
    $[68] = t23;
    $[69] = t26;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== menuOpen || $[73] !== t19 || $[74] !== t30) {
    t31 = <div className="absolute right-1 top-1/2 -translate-y-1/2 h-7 flex items-center"><Dropdown.Root open={menuOpen} onOpenChange={setMenuOpen}>{t19}{t30}</Dropdown.Root></div>;
    $[72] = menuOpen;
    $[73] = t19;
    $[74] = t30;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  let t32;
  if ($[76] !== t13 || $[77] !== t31) {
    t32 = <div className="group relative">{t13}{t31}</div>;
    $[76] = t13;
    $[77] = t31;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  return t32;
};
export default ChatHistoryMenu;
function _temp(w, i) {
  return <Shimmer key={i} className="rounded-md" height={20} width={w} />;
}
function _temp2(s) {
  return s.isStarred;
}
function _temp3(s_0) {
  return !s_0.isStarred;
}
function _temp4($) {
  return $.newChat;
}
function _temp5($_0) {
  return $_0.empty;
}
function _temp6($_1) {
  return $_1.starred;
}
function _temp7($_2) {
  return $_2.recent;
}
function _temp8($) {
  return $.untitled;
}
function _temp9($_0) {
  return $_0.sandbox;
}
function _temp10($_1) {
  return $_1.production;
}
function _temp11($_2) {
  return $_2.sandbox;
}
function _temp12($_3) {
  return $_3.production;
}
function _temp13($_4) {
  return $_4.actions.ariaLabel;
}
function _temp14($_5) {
  return $_5.actions.starred;
}
function _temp15($_6) {
  return $_6.actions.star;
}
function _temp16($_7) {
  return $_7.actions.rename;
}
function _temp17($_8) {
  return $_8.actions.delete;
}